import {
  CPTSlabForManualOption,
  DeliveryModes,
  TransactionType,
} from "../../../views/Orders/enums";
import { DeliveryOption, EnrichedSupplierForDelivery } from "../types";
import {
  getParentSupplierId,
  isMgpDistributorOrOutlet,
} from "./resolveDefaultEstimateSplitTransactionType";

export const TRADING_SELF_PICKUP_BLOCKED_MESSAGE =
  "Self Pickup is not available for Trading transactions except at Maruti distributor/outlet picking points. Please remove the Self Pickup delivery option(s) to continue.";

export interface SelfPickupDeliveryIndicator {
  cptSlab?: string | number | null;
  deliveryMode?: string | null;
  isDeleted?: boolean;
}

export interface TradingSelfPickupValidationContext {
  supplierId?: string | null;
  orderData?: any;
  mgpDistributorConfig?: unknown;
  parentSupplierId?: string;
  allSuppliers?: any[];
}

export function isTradingTransactionType(
  transactionType?: string | null,
): boolean {
  return transactionType === TransactionType.TRADING;
}

export function isSelfPickupDeliveryOption(
  delivery: SelfPickupDeliveryIndicator,
): boolean {
  const cptSlab = delivery.cptSlab;
  if (
    cptSlab === CPTSlabForManualOption.SELF_PICKUP ||
    cptSlab === String(CPTSlabForManualOption.SELF_PICKUP)
  ) {
    return true;
  }
  return delivery.deliveryMode === DeliveryModes.SELF_PICKUP;
}

export function splitHasSelfPickupDeliveryOptions(
  supplier: EnrichedSupplierForDelivery,
): boolean {
  const activeOptions = (supplier.deliveryOptions ?? []).filter(
    (option) => !(option as { isDeleted?: boolean }).isDeleted,
  );
  return activeOptions.some((option) => isSelfPickupDeliveryOption(option));
}

export function resolveParentSupplierIdForTradingSelfPickup(params: {
  supplierId?: string | null;
  orderData?: any;
  parentSupplierId?: string;
  allSuppliers?: any[];
}): string {
  const { supplierId, orderData, parentSupplierId, allSuppliers } = params;

  if (parentSupplierId) {
    return String(parentSupplierId);
  }

  if (supplierId && allSuppliers?.length) {
    const foundSupplier = allSuppliers.find(
      (supplier) => String(supplier._id) === String(supplierId),
    );
    if (foundSupplier?.parentSupplierId) {
      return String(foundSupplier.parentSupplierId);
    }
  }

  if (supplierId && orderData?.allocatedTo?.length) {
    const allocated = orderData.allocatedTo.find(
      (item: any) => String(item.supplierId) === String(supplierId),
    );
    if (allocated?.parentSupplierId) {
      return String(allocated.parentSupplierId);
    }
    if (allocated?.supplier?.parentSupplierId) {
      return String(allocated.supplier.parentSupplierId);
    }
  }

  return getParentSupplierId(supplierId ?? undefined, orderData);
}

/**
 * Trading + Self Pickup is allowed when the supplier PP belongs to the Maruti
 * distributor network (distributor or outlet), same MGP check used for FT.
 */
export function isTradingSelfPickupAllowed(
  supplierId: string | undefined | null,
  context: TradingSelfPickupValidationContext,
): boolean {
  if (!supplierId) {
    return false;
  }

  const parentSupplierId = resolveParentSupplierIdForTradingSelfPickup({
    supplierId,
    orderData: context.orderData,
    parentSupplierId: context.parentSupplierId,
    allSuppliers: context.allSuppliers,
  });

  return isMgpDistributorOrOutlet(
    supplierId,
    parentSupplierId,
    context.mgpDistributorConfig,
  );
}

export function buildTradingSelfPickupValidationContext(params: {
  supplierId?: string | null;
  orderData?: any;
  mgpDistributorConfig?: unknown;
  parentSupplierId?: string;
  allSuppliers?: any[];
}): TradingSelfPickupValidationContext {
  return {
    supplierId: params.supplierId,
    orderData: params.orderData,
    mgpDistributorConfig: params.mgpDistributorConfig,
    parentSupplierId: resolveParentSupplierIdForTradingSelfPickup(params),
    allSuppliers: params.allSuppliers,
  };
}

export function isSupplierSplitValidForTradingSelfPickup(
  supplier: EnrichedSupplierForDelivery,
  context?: TradingSelfPickupValidationContext,
): boolean {
  if (supplier.supplierId == null) {
    return true;
  }
  if (!isTradingTransactionType(supplier.transactionType)) {
    return true;
  }
  if (!splitHasSelfPickupDeliveryOptions(supplier)) {
    return true;
  }
  if (!context) {
    return false;
  }

  return isTradingSelfPickupAllowed(supplier.supplierId, {
    ...context,
    supplierId: supplier.supplierId,
  });
}

export function hasTradingSplitWithSelfPickupOptions(
  suppliers: EnrichedSupplierForDelivery[],
  baseContext?: Omit<
    TradingSelfPickupValidationContext,
    "supplierId" | "parentSupplierId"
  >,
): boolean {
  return suppliers.some((supplier) => {
    const context = buildTradingSelfPickupValidationContext({
      supplierId: supplier.supplierId,
      orderData: baseContext?.orderData,
      mgpDistributorConfig: baseContext?.mgpDistributorConfig,
      allSuppliers: baseContext?.allSuppliers,
    });
    return !isSupplierSplitValidForTradingSelfPickup(supplier, context);
  });
}

export function isTradingWithSelfPickupManualEntry(
  transactionType: string | undefined | null,
  cptSlab: CPTSlabForManualOption | undefined,
  context?: TradingSelfPickupValidationContext,
): boolean {
  if (
    !isTradingTransactionType(transactionType) ||
    cptSlab !== CPTSlabForManualOption.SELF_PICKUP
  ) {
    return false;
  }

  if (!context?.supplierId) {
    return true;
  }

  return !isTradingSelfPickupAllowed(context.supplierId, context);
}

export function getActiveDeliveryOptions(
  deliveryOptions: DeliveryOption[] | undefined,
): DeliveryOption[] {
  return (deliveryOptions ?? []).filter(
    (option) => !(option as { isDeleted?: boolean }).isDeleted,
  );
}
